import {createStore} from 'vuex';
import {clearAuthToken, getAuthToken, setAuthToken} from '../utils/auth-token';
import {deriveAccessContext, normalizeAccessContext} from '@/utils/access-context';

const normalizeSettings = function (value) {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? {...value}
        : {};
};

const syncRuntimeSettings = function (state) {
    if (typeof window === 'undefined') {
        return;
    }

    window.app = {
        ...(window.app || {}),
        ...(state.settings || {}),
    };
};

const syncAccessState = function (state) {
    state.access = normalizeAccessContext(
        state.user && state.user.access ? state.user.access : state.access,
        state.user || null
    );
    state.permissions = state.access && state.access.capabilities ? state.access.capabilities : {};
};

const bootstrapAccessContext = function () {
    if (typeof window === 'undefined' || !window.app || !window.app.access_context) {
        return deriveAccessContext();
    }

    return normalizeAccessContext(window.app.access_context);
};

const store = createStore({
    state: {
        user: false,
        permissions: {},
        settings: false,
        access: bootstrapAccessContext(),
        badges: {
            tickets: {
                enabled: false,
                count: 0,
                source: null,
            },
        },
    },
    mutations: {
        setSettings(state, data) {
            state.settings = normalizeSettings(data);
            syncRuntimeSettings(state);
            syncAccessState(state);
        },
        patchSettings(state, patch) {
            state.settings = {
                ...normalizeSettings(state.settings),
                ...normalizeSettings(patch),
            };
            syncRuntimeSettings(state);
            syncAccessState(state);
        },
        setAccessContext(state, access) {
            state.access = normalizeAccessContext(access, state.user || null);
            state.permissions = state.access && state.access.capabilities ? state.access.capabilities : {};
            if (state.user && typeof state.user === 'object') {
                state.user = {
                    ...state.user,
                    access: state.access,
                };
            }
        },
        setTicketBadge(state, payload) {
            const currentBadge = state.badges && state.badges.tickets
                ? state.badges.tickets
                : {enabled: false, count: 0, source: null};

            state.badges = {
                ...(state.badges || {}),
                tickets: {
                    ...currentBadge,
                    enabled: !!payload?.enabled,
                    count: Math.max(0, Number(payload?.count || 0)),
                    source: payload?.source ? String(payload.source) : null,
                },
            };
        },
        login(state, payload) {
            const response = payload && payload.response ? payload.response : payload;
            const remember = !(payload && Object.prototype.hasOwnProperty.call(payload, 'remember')) || !!payload.remember;
            state.user = response.user;
            syncAccessState(state);
            setAuthToken(response.token, remember);
            window.axios.defaults.headers.common['Authorization'] = 'Bearer ' + response.token;
        },
        logout(state) {
            const token = getAuthToken();
            const clearLocalSession = function () {
                state.user = false;
                state.permissions = {};
                syncAccessState(state);
                state.badges = {
                    ...(state.badges || {}),
                    tickets: {
                        enabled: false,
                        count: 0,
                        source: null,
                    },
                };
                delete window.axios.defaults.headers.common.Authorization;
                clearAuthToken();
            };

            // Clear local auth state immediately so route guards treat user as logged out.
            clearLocalSession();

            if (!token) {
                return;
            }

            axios.post('api/auth/logout', {}, {
                withCredentials: false,
                headers: {
                    Authorization: 'Bearer ' + token,
                }
            }).catch(function () {
                // Local session is already cleared.
            }).finally(function () {
                clearLocalSession();
            });
        },
        updateUser(state, response) {
            state.user = response;
            syncAccessState(state);
        },
    }
});

export default store;
